import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { filter, first, take } from 'rxjs/operators';

import { CloseChat, EnableHubtypeSuccess, ToggleChat } from '../store/chat.actions';
import { getHubtype } from '../store/chat.selectors';

import { CommonWindow } from '@core/domain/models/common-window.model';
import { StoreAndLanguage } from '@core/domain/state/core.state';
import { getLibraries, getStoreAndLanguage } from '@core/infrastructure/state/core.selectors';
import { CoreStorageService } from '@core/services/storage/storage.service';
import { PublicUser } from '@features/user';
import { SetFirstMessageTriggerAnalytics, ChatMessageAnalytics } from '@shared/analytics/store/app-events-analytics.actions';
import { getUserSelector } from '@shared/user/store/user.selectors';

@Injectable()
export class HubtypeService {
    public libraryLoaded$ = this.store.select(getLibraries).pipe(
        filter((libs) => libs?.botonic?.loaded),
        first()
    );

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        @Inject(WINDOW) private readonly window: CommonWindow,
        private readonly store: Store,
        private readonly zone: NgZone,
        private readonly storageService: CoreStorageService,
    ) {
        document.addEventListener('legacy-event-chat', () => {
            if (!inditex.getLocalStorage('hubtypeInUse')) {
                this.openChat();
            }
        });
    }

    public openChat(): void {
        this.store
            .select(getHubtype)
            .pipe(
                filter(hubtype => hubtype.loaded),
                first()
            )
            .subscribe(() => { this.store.dispatch(ToggleChat()); });
    }

    public initWidget(configuration: any, translations: any, user: PublicUser, storeAndLanguage: StoreAndLanguage): void {
        // Añadimos Hubtype en el DOM pasándole la configuración y el tipo
        const config = {
            ...configuration,
            menu: configuration.menu.map((element) => {
                return {
                    ...element,
                    ...element.label
                        ? { label: translations[element.label] }
                        : { closeLabel: translations[element.closeLabel] },
                };
            }),
        };

        // Definimos la configuración de Hubtype
        const botConfig = {
            appId: config.appId,
            theme: {
                userInput: {
                    box: {
                        placeholder: translations[config.placeHolder],
                    },
                },
                header: {
                    title: translations[config.title],
                },
                persistentMenu: config.persistentMenu ? config.menu : undefined,
            },
            onInit: (app): void => {
                this.onInitHubType(app, config, user, storeAndLanguage);
            },
            onOpen: (app): void => {
                this.onOpenHubType(app, config);
            },
            onClose: (_app): void => {
                this.onCloseHubType();
            },
        };

        const domElement = this.document.getElementById('chatbot-botonic');
        this.window.Botonic.render(domElement, botConfig);
    }

    private onInitHubType(app: any, config: any, user: PublicUser, storeAndLanguage: StoreAndLanguage): void {
        this.zone.run(() => {
            const data = this.getDataFromUser(user, storeAndLanguage);
            if (app?.updateUser) {
                app.updateUser(data);
            }

            // Comprobamos si el chat es de tipo Moca y además existe la función creada por el equipo de Hubtype
            if (config.isMoca && typeof mochatClientOnInit === 'function') {
                mochatClientOnInit();
            }

            this.store.dispatch(EnableHubtypeSuccess());
        });
    }

    private onOpenHubType(app: any, config: any): void {
        this.zone.run(() => {
            app.addUserPayload('POSTBACK_GET_STARTED_BUTTON');

            // Comprobamos si el chat es de tipo Moca y además existe la función creada por el equipo de Hubtype
            if (config.isMoca && typeof mochatClientOnOpen === 'function') {
                mochatClientOnOpen();
            }

            this.enableMessageHandling(config.isMoca);
        });
    }

    private onCloseHubType(): void {
        this.zone.run(() => {
            this.store.dispatch(CloseChat());
            // Quitamos del storage el control de uso
            this.storageService.removeValue('hubtypeInUse');

            this.store.dispatch(SetFirstMessageTriggerAnalytics({ pending: false }));
            this.window.Botonic.onMessage = undefined;
        });
    }

    /**
     * Función para comprobar si Hubtype está activo
     */

    /**
     * Función para obtener los datos del usuario actual
     */
    public getDataFromUser(user, storeAndLanguage: StoreAndLanguage): any {
        let username = '';
        let loginId = '';
        let email = '';

        if (user) {
            if (user.firstName && user.lastName) {
                username = `${user.firstName} ${user.lastName}`;
            }

            if (user.identity.userId) {
                loginId = user.identity.userId;
            }

            if (user.email) {
                email = user.email;
            }
        }

        return {
            extra_data: {
                store: storeAndLanguage.countryCode,
                language: storeAndLanguage.languageCode,
                name: username,
                login: loginId,
                mail: email,
            },
        };
    }

    public updateUser(): void {
        const libraryLoaded$ = this.store.select(getLibraries).pipe(filter((libs) => libs.botonic.loaded));
        const user$ = this.store.select(getUserSelector).pipe(filter(user => !!user));
        const storeAndLanguage$ = this.store.select(getStoreAndLanguage).pipe(take(1));

        combineLatest([libraryLoaded$, user$, storeAndLanguage$]).subscribe(() => { });
    }

    public checkIfHubtypeIsActive(config, languageId: string): boolean {
        if (!config.active) {
            return false;
        }
        // Comprueba si debe estar activo en el idioma actual
        let isSupportedLanguage = false;

        if (config?.languages.indexOf(languageId.toString()) !== -1) {
            isSupportedLanguage = true;
        }

        if (isSupportedLanguage) {
            return true;
        }
        return false;
    }

    public enableMessageHandling(isMoca: boolean): void {
        this.zone.run(() => {
            this.store.dispatch(SetFirstMessageTriggerAnalytics({ pending: true }));
        });

        this.window.Botonic.onMessage = (_chatInstance, message): void => {
            this.zone.run(() => {
                this.store.dispatch(ChatMessageAnalytics(message));
            });
            if (isMoca) {
                mochatClientOnMessage(message);
            }
        };
    }
}
